import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { VpnMe, VpnMySession, VpnPendingGrant } from '../api/types'
import { QrCode } from '../components/QrCode'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { formatDateTime, formatTime } from '../lib/format'
import { aliasExample, toAlias } from '../lib/vpnLan'
import { buildTechnicianConfig, generateWireGuardKeyPair } from '../lib/wireguard'

/**
 * Montørens egen VPN-profil (beslutning 091).
 *
 * Nøglen laves HER i browseren. Den private del bliver kun i siden, mens konfigurationen vises, og
 * sendes aldrig til serveren. Lukkes siden, er den væk - så laves der bare en ny.
 *
 * Profilen giver ingen adgang i sig selv. Adgang til et kundeanlæg gives af en Superadmin og lukker
 * af sig selv igen.
 */
export function VpnProfilePage() {
  const queryClient = useQueryClient()
  const me = useQuery({ queryKey: ['vpn-me'], queryFn: () => api.get<VpnMe>('/api/vpn/me'), refetchInterval: 60_000 })
  const [config, setConfig] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionBusy, setSessionBusy] = useState<number | null>(null)
  const [sessionMessage, setSessionMessage] = useState<{ ok: boolean; text: string } | null>(null)

  /** Åbner en godkendt adgang - med sikkerhedsnøgle, når det kræves (beslutning 092). */
  async function activate(id: number) {
    setSessionBusy(id)
    setSessionMessage(null)
    try {
      const updated = await api.post<VpnMe>(`/api/vpn/me/sessions/${id}/activate`)
      queryClient.setQueryData(['vpn-me'], updated)
      setSessionMessage({ ok: true, text: 'Du er forbundet. Slå tunnelen til i WireGuard-appen.' })
    } catch (err) {
      setSessionMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke åbne adgangen.' })
    } finally {
      setSessionBusy(null)
    }
  }

  async function stop(id: number) {
    if (!window.confirm('Luk din adgang nu? Du skal have en ny godkendelse for at komme ind igen.')) return
    setSessionBusy(id)
    setSessionMessage(null)
    try {
      const updated = await api.delete<VpnMe>(`/api/vpn/me/sessions/${id}`)
      queryClient.setQueryData(['vpn-me'], updated)
      setSessionMessage({ ok: true, text: 'Adgangen er lukket.' })
    } catch (err) {
      setSessionMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke lukke adgangen.' })
    } finally {
      setSessionBusy(null)
    }
  }

  async function createKey() {
    if (
      me.data?.hasProfile &&
      !window.confirm('Lav en ny nøgle? Den gamle holder op med at virke med det samme, og en åben adgang lukkes.')
    ) {
      return
    }

    setBusy(true)
    setError(null)
    setConfig(null)
    try {
      const keys = await generateWireGuardKeyPair()
      // Kun den offentlige nøgle sendes.
      const profile = await api.put<VpnMe>('/api/vpn/me', { publicKey: keys.publicKey })
      if (!profile.vpnAddress || !profile.endpoint) {
        throw new Error('Gatewayen svarede uden adresse. Prøv igen om lidt.')
      }
      setConfig(buildTechnicianConfig(keys.privateKey, profile.vpnAddress, profile.endpoint))
      queryClient.setQueryData(['vpn-me'], profile)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke lave nøglen.')
    } finally {
      setBusy(false)
    }
  }

  function download() {
    if (!config) return
    const url = URL.createObjectURL(new Blob([config], { type: 'text/plain' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'tc-vpn.conf'
    link.click()
    URL.revokeObjectURL(url)
  }

  if (me.isLoading) return <Loading />
  if (me.error) return <ErrorBox error={me.error} />
  const data = me.data!

  return (
    <div className="stack" style={{ gap: 16 }}>
      {config ? (
        <Card title="Din VPN-konfiguration">
          <p className="small" style={{ marginTop: 0 }}>
            <strong>Vises kun nu.</strong> Den private nøgle gemmes ingen steder - lukker du siden, før den er lagt ind i
            WireGuard-appen, skal du bare lave en ny.
          </p>
          <ol className="small" style={{ paddingLeft: 18 }}>
            <li>
              Installér appen <strong>WireGuard</strong> på telefonen eller computeren: <WireGuardLinks />
            </li>
            <li>
              <strong>På telefonen:</strong> tryk "+" og "Scan QR-kode", og scan koden herunder fra en anden skærm.
              Eller tryk "Hent fil" og importér filen i appen.
            </li>
            <li>Slå tunnelen til, når du har fået adgang til et anlæg.</li>
          </ol>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
            <QrCode value={config} size={260} errorCorrectionLevel="L" />
          </div>
          <div className="btn-group">
            <button className="btn" onClick={download}>
              Hent fil
            </button>
            <button className="btn btn--primary" onClick={() => setConfig(null)}>
              Den er lagt ind - skjul
            </button>
          </div>
        </Card>
      ) : null}

      <Card title="VPN-adgang til kundeanlæg">
        {data.gatewayError && <div className="error-box" style={{ marginBottom: 8 }}>{data.gatewayError}</div>}

        {!data.hasProfile && (
          <p className="small" style={{ marginTop: 0 }}>
            Du har ingen VPN-profil. Lav en nøgle for at komme i gang - det giver ikke adgang til noget endnu. Adgang til et
            anlæg gives af en Superadmin og lukker af sig selv igen.
          </p>
        )}

        {data.hasProfile && data.revoked && (
          <div className="error-box" style={{ marginBottom: 8 }}>
            Din profil er spærret (fx efter en mistet telefon). Lav en ny nøgle for at bruge VPN igen.
          </div>
        )}

        <p className="small" style={{ marginTop: 0 }}>
          Du skal bruge appen <strong>WireGuard</strong>: <WireGuardLinks />
        </p>

        {data.hasProfile && !data.revoked && (
          <p className="small" style={{ marginTop: 0 }}>
            Din VPN-adresse er <code>{data.vpnAddress}</code>. Sidst forbundet: {formatDateTime(data.lastHandshakeAt)}.
          </p>
        )}

        {data.hasProfile && !data.revoked && data.configMissingLan && (
          <div className="error-box" style={{ marginBottom: 8 }}>
            Din WireGuard-konfiguration er fra før LAN-adgang og kan ikke nå udstyret bag routerne. Tryk "Lav ny nøgle" og læg
            den nye konfiguration i appen.
          </div>
        )}

        {error && <div className="error-box">{error}</div>}

        <button className="btn btn--primary" style={{ marginTop: 8 }} disabled={busy} onClick={() => void createKey()}>
          {busy ? 'Laver nøgle ...' : data.hasProfile ? 'Lav ny nøgle' : 'Lav VPN-nøgle'}
        </button>
        {data.stepUpRequired && (
          <p className="tiny muted">🔑 At lave en nøgle og åbne adgang kræver din YubiKey.</p>
        )}
        {data.hasProfile && (
          <p className="tiny muted">Ny telefon, eller er konfigurationen væk? Lav en ny nøgle - den gamle holder op med at virke.</p>
        )}
      </Card>

      {(data.pendingGrants?.length ?? 0) > 0 && (
        <ConnectCard
          grants={data.pendingGrants!}
          connectedTo={data.activeSessions[0]?.siteName ?? null}
          canConnect={data.hasProfile && !data.revoked}
          stepUpRequired={data.stepUpRequired}
          busyId={sessionBusy}
          onConnect={(id) => void activate(id)}
        />
      )}

      <Card title="Din adgang lige nu">
        {sessionMessage && (
          <div
            className={sessionMessage.ok ? 'small' : 'error-box'}
            style={{ marginBottom: 8, color: sessionMessage.ok ? 'var(--success)' : undefined }}
          >
            {sessionMessage.text}
          </div>
        )}
        {data.activeSessions.length === 0 ? (
          <EmptyState>Du har ikke adgang til nogen anlæg lige nu.</EmptyState>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            {data.activeSessions.map((s) => (
              <div key={s.id} className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div className="small">
                  <strong>{s.siteName}</strong>
                  {s.customerName && ` hos ${s.customerName}`} - åben til kl. {formatTime(s.expiresAt)}
                  {s.routerAddress && (
                    <div className="tiny muted">
                      Routeren: <code>{s.routerAddress}</code>
                    </div>
                  )}
                  {s.lanPrefix && s.customerLan && <LanHelp session={s} />}
                </div>
                <button className="btn btn--danger" disabled={sessionBusy !== null} onClick={() => void stop(s.id)}>
                  {sessionBusy === s.id ? 'Lukker ...' : 'Stop adgang'}
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

/**
 * Hvor WireGuard hentes. Kun de officielle kilder: App Store, Google Play og wireguard.com, hvor
 * Windows, Mac og Linux findes.
 */
const wireGuardDownloads = [
  { label: 'iPhone/iPad', href: 'https://apps.apple.com/app/wireguard/id1441195209' },
  { label: 'Android', href: 'https://play.google.com/store/apps/details?id=com.wireguard.android' },
  { label: 'Windows, Mac og Linux', href: 'https://www.wireguard.com/install/' }
]

function WireGuardLinks() {
  return (
    <>
      {wireGuardDownloads.map((d, i) => (
        <span key={d.href}>
          {i > 0 && ' · '}
          <a href={d.href} target="_blank" rel="noopener noreferrer">
            {d.label}
          </a>
        </span>
      ))}
    </>
  )
}

/**
 * Montøren vælger selv, hvilket anlæg han forbinder til: først kunde, så anlæg blandt dem, en Superadmin har godkendt.
 * Intet åbner, før han trykker "Forbind" (med YubiKey, når det kræves). Kun ét anlæg ad gangen.
 */
function ConnectCard({
  grants,
  connectedTo,
  canConnect,
  stepUpRequired,
  busyId,
  onConnect
}: {
  grants: VpnPendingGrant[]
  connectedTo: string | null
  canConnect: boolean
  stepUpRequired: boolean
  busyId: number | null
  onConnect: (id: number) => void
}) {
  const customers = [...new Set(grants.map((g) => g.customerName ?? 'Uden kunde'))].sort((a, b) => a.localeCompare(b, 'da'))
  const [customer, setCustomer] = useState(customers.length === 1 ? customers[0] : '')
  const sites = grants.filter((g) => (g.customerName ?? 'Uden kunde') === customer)
  const [grantId, setGrantId] = useState<number | null>(null)
  const chosen = sites.find((g) => g.id === grantId) ?? (sites.length === 1 ? sites[0] : undefined)

  return (
    <Card title="Forbind til et anlæg">
      <p className="small" style={{ marginTop: 0 }}>
        Vælg kunden og anlægget, du skal arbejde på. Adgangen åbner først, når du trykker "Forbind"
        {stepUpRequired && <> og bekræfter med din <strong>YubiKey</strong></>} - og tiden løber fra da.
      </p>

      {connectedTo && (
        <div className="small" style={{ marginBottom: 8, color: '#B45309' }}>
          Du er forbundet til <strong>{connectedTo}</strong>. Tryk "Stop adgang" herunder, før du forbinder til et andet anlæg.
        </div>
      )}

      <div className="form-grid">
        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Kunde</span>
          <select
            value={customer}
            onChange={(e) => {
              setCustomer(e.target.value)
              setGrantId(null)
            }}
          >
            <option value="">Vælg kunde ...</option>
            {customers.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Anlæg</span>
          <select value={chosen?.id ?? ''} disabled={!customer} onChange={(e) => setGrantId(Number(e.target.value) || null)}>
            <option value="">Vælg anlæg ...</option>
            {sites.map((g) => (
              <option key={g.id} value={g.id}>
                {g.siteName} ({formatDuration(g.durationMinutes)}){g.lanPrefix ? ` - ${g.lanPrefix}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {chosen && (
        <>
          <div className="small" style={{ marginTop: 8 }}>
            {chosen.routerAddress && (
              <>
                Routeren: <code>{chosen.routerAddress}</code>
              </>
            )}
            {chosen.lanPrefix && chosen.customerLan && (
              <div>
                Kundens LAN <code>{chosen.customerLan}</code> nås som <code>{chosen.lanPrefix}</code>
                {aliasExample(chosen.customerLan, chosen.lanPrefix) && <> (fx <code>{aliasExample(chosen.customerLan, chosen.lanPrefix)}</code>)</>}
                {chosen.routerLanAddress && <> · udstyret ser dig som <code>{chosen.routerLanAddress}</code></>}
              </div>
            )}
            {!chosen.lanPrefix && <div className="muted">LAN-adgang er ikke sat op på anlægget - du kan kun nå routeren.</div>}
          </div>
          <div className="tiny muted" style={{ marginTop: 4 }}>
            {chosen.reason}
            {chosen.grantedByName && ` · godkendt af ${chosen.grantedByName}`} · skal åbnes inden {formatDateTime(chosen.activateBefore)}
          </div>
        </>
      )}

      <button
        className="btn btn--primary"
        style={{ marginTop: 12 }}
        disabled={!chosen || !canConnect || !!connectedTo || busyId !== null}
        onClick={() => chosen && onConnect(chosen.id)}
      >
        {chosen && busyId === chosen.id ? 'Forbinder ...' : stepUpRequired ? '🔑 Forbind' : 'Forbind'}
      </button>
      {!canConnect && <p className="tiny muted">Lav din VPN-nøgle først - så kan du forbinde.</p>}
    </Card>
  )
}

/**
 * Kundens LAN nås gennem sitets alias (beslutning 094): montøren skriver alias-adressen, routeren oversætter.
 * Feltet regner en adresse fra kundens dokumentation om, så montøren ikke skal gøre det i hovedet.
 */
function LanHelp({ session }: { session: VpnMySession }) {
  const [address, setAddress] = useState('')
  const alias = address.trim() ? toAlias(address, session.customerLan!, session.lanPrefix!) : null

  return (
    <div className="tiny" style={{ marginTop: 4 }}>
      Kundens LAN <code>{session.customerLan}</code> nås som <code>{session.lanPrefix}</code> - samme sidste tal
      {aliasExample(session.customerLan!, session.lanPrefix!) && <> (fx <code>{aliasExample(session.customerLan!, session.lanPrefix!)}</code>)</>}.
      {session.routerLanAddress && (
        <>
          {' '}
          Udstyret ser dig som <code>{session.routerLanAddress}</code>.
        </>
      )}
      <div className="row" style={{ gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
        <input
          value={address}
          placeholder="Kundens adresse, fx 10.122.1.50"
          style={{ maxWidth: 220 }}
          onChange={(e) => setAddress(e.target.value)}
        />
        {address.trim() && (alias ? <span>Skriv <code>{alias}</code></span> : <span className="muted">Ikke på kundens LAN</span>)}
      </div>
    </div>
  )
}

function formatDuration(minutes: number) {
  return minutes < 60 ? `${minutes} minutter` : minutes === 60 ? '1 time' : `${minutes / 60} timer`
}
